import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Alert, 
  Button, 
  Form, 
  FormGroup, 
  Label, 
  Input,
  Table,
  Badge,
  Spinner,
  Row,
  Col
} from 'reactstrap';
import axios from '../../api/Axios';

const Subjects = () => {
  const [category, setCategory] = useState('STEM');
  const [subjectName, setSubjectName] = useState('');
  const [branches, setBranches] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(true);

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      setLoadingSubjects(true);
      const response = await axios.get('/api/subjects/');
      setSubjects(response.data);
    } catch (err) {
      console.error('Error fetching subjects:', err);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate inputs
    if (!subjectName.trim()) {
      setError('Please enter a subject name');
      return;
    }

    // Parse branches (comma-separated) - now optional
    const branchList = branches
      .split(',')
      .map(b => b.trim())
      .filter(b => b.length > 0);

    setLoading(true);

    try {
      const payload = {
        category: category,
        subject_name: subjectName.trim(),
        branches: branchList
      };

      const response = await axios.post('/api/subjects/create-with-branches/', payload);
      
      // Use the message from the backend
      const message = response.data.message || 'Operation completed successfully';
      setSuccess(message);
      
      setSubjectName('');
      setBranches('');
      
      // Refresh the subjects list
      fetchSubjects();
    } catch (err) {
      console.error('Error creating subject:', err);
      setError(err.response?.data?.subject_name?.[0] || err.response?.data?.detail || 'Failed to process request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubject = async (subjectId, subjectName) => {
    if (!window.confirm(`Are you sure you want to delete "${subjectName}" and all its branches?`)) {
      return;
    }

    try {
      await axios.delete(`/api/subjects/${subjectId}/`);
      setSuccess(`Subject "${subjectName}" deleted successfully`);
      fetchSubjects();
    } catch (err) {
      console.error('Error deleting subject:', err);
      setError('Failed to delete subject. Please try again.');
    }
  };

  const handleDeleteBranch = async (branchId, branchName, subjectName) => {
    if (!window.confirm(`This branch will be deleted from "${subjectName}". Continue?`)) {
      return;
    }

    try {
      await axios.delete(`/api/branches/${branchId}/`);
      setSuccess(`Branch "${branchName}" deleted successfully`);
      fetchSubjects();
    } catch (err) {
      console.error('Error deleting branch:', err);
      setError('Failed to delete branch. Please try again.');
    }
  };

  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col">
          <h2 className="mb-0">
            <i className="bi bi-book me-2"></i>
            Manage Subjects
          </h2>
          <p className="text-muted">Add and manage subjects and their branches</p>
        </div>
      </div>

      <Row>
        <Col lg="5">
          {/* Create Subject Form */}
          <Card className="mb-4">
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-plus-circle me-2"></i>
                Add Subject or Branch
              </CardTitle>

              {error && (
                <Alert color="danger" toggle={() => setError('')}>
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert color="success" toggle={() => setSuccess('')}>
                  <i className="bi bi-check-circle me-2"></i>
                  {success}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label for="category">
                    <strong>Category</strong>
                  </Label>
                  <Input
                    type="select"
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="STEM">STEM</option>
                    <option value="ARTS">Arts</option>
                  </Input>
                  <small className="text-muted">Select the category for this subject</small>
                </FormGroup>

                <FormGroup>
                  <Label for="subjectName">
                    <strong>Subject Name</strong>
                  </Label>
                  <Input
                    type="text"
                    id="subjectName"
                    placeholder="e.g., Mathematics"
                    value={subjectName}
                    onChange={(e) => setSubjectName(e.target.value)}
                  />
                  <small className="text-muted">Enter the name of the subject</small>
                </FormGroup>

                <FormGroup>
                  <Label for="branches">
                    <strong>Branches</strong> <small className="text-muted">(Optional)</small>
                  </Label>
                  <Input
                    type="textarea"
                    id="branches"
                    rows="4"
                    placeholder="e.g., Algebra, Geometry, Calculus"
                    value={branches}
                    onChange={(e) => setBranches(e.target.value)}
                  />
                  <small className="text-muted">
                    Enter branch names separated by commas, or leave empty to add subject only
                  </small>
                </FormGroup>

                <Button 
                  color="primary" 
                  type="submit" 
                  disabled={loading}
                  block
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-plus-circle me-2"></i>
                      Add Subject / Branch
                    </>
                  )}
                </Button>
              </Form>
            </CardBody>
          </Card>
        </Col>

        <Col lg="7">
          {/* Subjects List */}
          <Card>
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-list-ul me-2"></i>
                Existing Subjects
                {subjects.length > 0 && (
                  <Badge color="primary" pill className="ms-2">
                    {subjects.length}
                  </Badge>
                )}
              </CardTitle>

              {loadingSubjects ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-3 text-muted">Loading subjects...</p>
                </div>
              ) : subjects.length === 0 ? (
                <Alert color="info" className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  No subjects found. Create your first subject above.
                </Alert>
              ) : (
                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {subjects.map((subject) => (
                    <Card key={subject.id} className="mb-3">
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <h6 className="mb-1">
                              {subject.name}
                              <Badge 
                                color={subject.category === 'STEM' ? 'info' : 'warning'} 
                                className="ms-2"
                              >
                                {subject.category_display}
                              </Badge>
                            </h6>
                          </div>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleDeleteSubject(subject.id, subject.name)}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                        
                        {subject.branches && subject.branches.length > 0 ? (
                          <div>
                            <small className="text-muted d-block mb-2">
                              <strong>Branches ({subject.branches.length}):</strong>
                            </small>
                            <div className="d-flex flex-wrap gap-2">
                              {subject.branches.map((branch) => (
                                <Badge 
                                  key={branch.id} 
                                  color="secondary" 
                                  pill
                                  className="d-flex align-items-center"
                                  style={{ paddingRight: '0.3rem' }}
                                >
                                  <span>{branch.name}</span>
                                  <button
                                    type="button"
                                    className="btn-close btn-close-white ms-2"
                                    style={{ 
                                      fontSize: '0.6rem',
                                      padding: '0.2rem',
                                      opacity: 0.7
                                    }}
                                    onClick={() => handleDeleteBranch(branch.id, branch.name, subject.name)}
                                    title="Delete this branch"
                                  />
                                </Badge>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <small className="text-muted">No branches</small>
                        )}
                      </CardBody>
                    </Card>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Subjects;
